import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CompanyService } from '../../../../core/services/company.service';
import { ICompany } from '../../../../core/models/ICompany';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IUser } from '../../../../core/models/IUser';
import { UserService } from '../../../../core/services/user.service';
import { environment } from '../../../../../environments/environment';
import { CommentService } from '../../../../core/services/comment.service';
import { CommentList } from "../../comment/comment-list/comment-list";
import { Email } from "../../popups/email/email";
import { MatDialog } from '@angular/material/dialog';
import { LogService } from '../../../../core/services/log.service';

@Component({
  selector: 'app-company-details',
  imports: [
    CommonModule,
    FormsModule,
    CommentList
],
  templateUrl: './company-details.html',
  styleUrl: './company-details.scss'
})
export class CompanyDetails implements OnInit {

  currentUser: IUser | null | undefined;
  company?: ICompany;
  isLoading = true;
  lightboxOpen = false;
  currentImage = 0;
  selectedScores: Record<RatingField, number> = { 
    quality: 0, 
    price: 0, 
    punctuality: 0, 
    communication: 0 
  };
  newComment: string = '';

  apiUrl = environment.apiUrl

  constructor(
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private commentService: CommentService,
    private userService: UserService,
    private dialog: MatDialog,
    private logger: LogService
  ) {
  }
  
  ngOnInit(): void {
    this.userService.user$.subscribe(user => {
      this.currentUser = user;
      if (this.company && this.hasUserRating()) {
        this.selectedScores = this.company.ratings.find(r=>r.user === user?._id)?.scores ?? this.selectedScores;
      }
    });
    const id = this.route.snapshot.paramMap.get('id')!;
    this.companyService.getCompanyById(id, true).subscribe({
      next: (data) => {
        this.company = data;
        this.isLoading = false;
        if(this.hasUserRating()){
          this.selectedScores = data.ratings.find(r=>r.user === this.currentUser?._id)?.scores ?? this.selectedScores;
        }
      },
      error: (err) => {
        this.logger.error('Erreur de chargement de la company: ' + JSON.stringify(err));
        this.isLoading = false;
      }
    });
  }

  openLightbox(index: number) {
    this.currentImage = index;
    this.lightboxOpen = true;
  }

  closeLightbox() {
    this.lightboxOpen = false;
  }

  nextImage(event: Event) {
    event.stopPropagation();
    this.currentImage = (this.currentImage + 1) % this.company!.gallery.length;
  }

  prevImage(event: Event) {
    event.stopPropagation();
    this.currentImage =
      (this.currentImage - 1 + this.company!.gallery.length) 
      % this.company!.gallery.length;
  }

  setScore(field: RatingField, value: number) {
    this.selectedScores[field] = value;
  }


  submitRating(event: Event) {
    if (!this.currentUser) {
      alert('Veuillez vous connecter pour évaluer cette entreprise.');
      return;
    }
    event.preventDefault();

    // Vérifier que toutes les notes sont remplies
    const scores = this.selectedScores;
    const allFilled = Object.values(scores).every(v => v >= 1 && v <= 5);

    if (!allFilled) {
      alert("Veuillez noter tous les critères.");
      return;
    }

    this.companyService.rateCompany(this.company!._id, scores).subscribe({
      next: (response) => {
        this.company!.ratings = response.ratings;
        this.company!.averageRating = response.averageRating;
      },
      error: (err) => {
        this.logger.error('Erreur lors de la soumission de la note: ' + JSON.stringify(err));
      }
    });
  }

  submitComment(event: Event) {
    if (!this.currentUser) {
      alert('Veuillez vous connecter pour publier un commentaire.');
      return;
    }
    event.preventDefault();
    if (this.newComment.trim()) {
      this.commentService.addComment(this.company!._id, this.newComment.trim()).subscribe({
        next: (comment) => {
          this.company!.comments.push(comment);
          this.newComment = '';
        },
        error: (err) => {
          this.logger.error('Erreur lors de la soumission du commentaire: ' + JSON.stringify(err));
        }
      });
    }
  }

  hasUserRating(): boolean {
    if(!this.company) return false;
    return this.company.ratings.some(rating => rating.user === this.currentUser?._id);
  }

  toggleFavorite(company: ICompany) {
    if (!this.currentUser) {
      alert('Veuillez vous connecter pour gérer vos favoris.');
      return;
    }
    if (this.currentUser.favorites.includes(company._id)) {
      this.userService.removeFromFavorites(company._id).subscribe({
        next: () => {
          this.currentUser = this.userService.currentUser;
        },
        error: (err) => {
          this.logger.error('Erreur lors de la suppression des favoris: ' + JSON.stringify(err));
        }
      });
    } else {
      this.userService.addToFavorites(company._id).subscribe({
        next: () => {
          this.currentUser = this.userService.currentUser;
        },
        error: (err) => {
          this.logger.error('Erreur lors de l\'ajout aux favoris: ' + JSON.stringify(err));
        }
      });
    }
  }

  openEmailDialog(from:string, email: string) { 
    if (!this.currentUser) {
      alert('Veuillez vous connecter pour contacter cette entreprise.');
      return;
    }
    this.dialog.open(
      Email, 
      { 
        width: '450px', 
        data: { from, to: email } 
      }
    ); 
  }
}

type RatingField = 'quality' | 'price' | 'punctuality' | 'communication';